/**
 * Carer-guidance markdown parser — TS port of
 * `app.outcomemonitoring/ios/OutcomeMonitoring/Utilities/GuidanceMarkdownParser.swift`.
 *
 * Same algorithm both sides so the librarian (web) and the carer (iOS)
 * agree on what folds and where. Storage shape lives in
 * `_activityDefinition.documentation[i].display` (one markdown string
 * per level) and is unchanged by this work — only the renderers differ.
 *
 * Filed under OMON-068 (the design ticket) and pairs with OMON-082's
 * iOS implementation. Conventions for authoring this markdown shape
 * live in `reference/GUIDANCE-AUTHORING-CONVENTIONS.md` (OMON-084).
 */

export interface GuidanceSection {
  heading: string;
  /** Stable slug used as the fold-state key. Lowercased, alphanumeric
   *  runs joined by `-`, with leading/trailing `-` stripped. */
  key: string;
  body: string;
}

export interface ParsedGuidanceLevel {
  /** Lines between the H1 + subtitle and the first `## ` heading.
   *  Typically the per-level descriptor like
   *  "Current level: 1 - Support worker undertook task -
   *   Working towards: Level 2". */
  preamble: string;
  sections: GuidanceSection[];
}

/**
 * Split a level's markdown into preamble + H2 sections.
 *
 * Input shape (from `_activityDefinition.documentation[].display`):
 *
 *     # <Goal title>
 *     <subtitle>
 *     <preamble line(s)>
 *
 *     ## H2 HEADING
 *     <body>
 *
 *     ## ANOTHER H2
 *     <body>
 *
 * The H1 line is dropped (redundant with the goal title shown in the
 * detail header). The first non-blank line after the H1 is also
 * dropped (redundant with the row's Goal Sub-category label).
 * Anything remaining before the first `## ` becomes the preamble.
 * Each `## …` starts a new section; the body runs until the next
 * `## …` or end-of-input.
 */
export function parse(markdown: string): ParsedGuidanceLevel {
  const lines = markdown.split("\n");

  let idx = 0;

  // Drop H1 line.
  if (idx < lines.length && lines[idx].startsWith("# ")) {
    idx += 1;
  }

  // Skip blank lines.
  while (idx < lines.length && lines[idx].trim() === "") {
    idx += 1;
  }

  // Drop subtitle line (first non-blank line after H1, unless it's an H2).
  if (idx < lines.length && !lines[idx].startsWith("## ")) {
    idx += 1;
  }

  return parseBody(lines.slice(idx));
}

/**
 * Parse HEADERLESS guidance — preamble + `## ` sections, with no `# title` /
 * subtitle convention. Monitored-event carer guidance
 * (`_observationDefinition._monitoredEventGuidance`, Zammad #49088) is stored
 * this way: one text per event, `## HEADING` sections, no H1. `parse()` must
 * not be used for it — its positional subtitle drop would eat the first
 * content line of any text that opens with a preamble.
 */
export function parseSections(markdown: string): ParsedGuidanceLevel {
  return parseBody(markdown.split("\n"));
}

/** Preamble + H2 sections from the lines after any header has been dropped. */
function parseBody(lines: string[]): ParsedGuidanceLevel {
  let idx = 0;

  // Skip leading blank lines.
  while (idx < lines.length && lines[idx].trim() === "") {
    idx += 1;
  }

  // Preamble: lines up to first `## `.
  const preambleLines: string[] = [];
  while (idx < lines.length && !lines[idx].startsWith("## ")) {
    preambleLines.push(lines[idx]);
    idx += 1;
  }
  const preamble = preambleLines.join("\n").trim();

  // Sections: each `## heading` starts one.
  const sections: GuidanceSection[] = [];
  while (idx < lines.length) {
    if (!lines[idx].startsWith("## ")) {
      idx += 1;
      continue;
    }
    const heading = lines[idx].slice(3).trim();
    idx += 1;
    const bodyLines: string[] = [];
    while (idx < lines.length && !lines[idx].startsWith("## ")) {
      bodyLines.push(lines[idx]);
      idx += 1;
    }
    const body = bodyLines.join("\n").trim();
    sections.push({ heading, key: slug(heading), body });
  }

  return { preamble, sections };
}

/**
 * Strip the H1 + subtitle header from a stored guidance markdown.
 *
 * The stored shape is `# <title>\n<subcategory display>\n\n<editable…>`.
 * The H1 + subtitle are dead data — `parse()` drops them on render
 * (they're redundant with the goal title field + subcategory display
 * shown in the detail header). This helper returns just the editable
 * tail so the librarian edit textarea can show only what's actually
 * meaningful to change (OMON-098).
 *
 * Idempotent — if the input has no H1, the output is identical to the
 * input (no false subtitle-drop on headerless content).
 */
export function stripGuidanceHeader(markdown: string): string {
  const lines = markdown.split("\n");
  let idx = 0;
  if (idx < lines.length && lines[idx].startsWith("# ")) {
    idx += 1;
    // Drop the subtitle line POSITIONALLY — the single line directly after the
    // H1, even when it is blank. `applyGuidanceHeader` always emits
    // `# title\n<subtitle>\n\n<content>`, so a fact with an empty subtitle
    // (parent facts carry no subcategory) writes `# title\n\n\n<content>`.
    // Skipping blank lines *before* the subtitle drop (the old behaviour) would
    // step over that empty subtitle line and eat the first CONTENT line as the
    // "subtitle" — round-tripping the field to "" and blanking the editor
    // textarea on every tab switch (OMON-242, 2026-07-07). Guard on `## ` so a
    // header-less-subtitle fact whose content opens with an H2 keeps that
    // heading.
    if (idx < lines.length && !lines[idx].startsWith("## ")) idx += 1;
    while (idx < lines.length && lines[idx].trim() === "") idx += 1;
  }
  return lines.slice(idx).join("\n");
}

/**
 * Reconstruct the stored shape by prepending the H1 + subtitle to the
 * editable tail. Used on every textarea write-back so that storage
 * keeps the convention even though the librarian never types the
 * header themselves. Title + subtitle are taken from current state,
 * so renaming a goal or moving its subcategory auto-heals the header.
 */
export function applyGuidanceHeader(content: string, title: string, subtitle: string): string {
  return `# ${title}\n${subtitle}\n\n${content}`;
}

/**
 * Stable slug for use as a fold-state key. Same shape as the
 * catalogue's slugify and the Swift parser's slug — lowercased,
 * alphanumeric runs joined by `-`, with leading/trailing `-`
 * stripped.
 */
export function slug(text: string): string {
  const lowered = text.toLowerCase();
  let result = "";
  let lastWasHyphen = false;
  for (const ch of lowered) {
    const isAlnum = /[a-z0-9]/.test(ch);
    if (isAlnum) {
      result += ch;
      lastWasHyphen = false;
    } else if (!lastWasHyphen && result.length > 0) {
      result += "-";
      lastWasHyphen = true;
    }
  }
  while (result.endsWith("-")) {
    result = result.slice(0, -1);
  }
  return result;
}
